/**
 * Cross-fade between the first and the second photo of a product card (QA of
 * 7 Oct: «the transition is very fast», with buckmason.com as the reference,
 * measured there as `opacity 0.2s ease-in-out` on the second image).
 *
 * Horizon has no transition to slow down. The card's gallery is a scroll-snap
 * slideshow, and assets/product-card.js:479-511 answers pointerenter and
 * pointerleave with `slideshow.next/previous(undefined, { animate: false })`:
 * an instant scroll, so the photo is swapped in one frame.
 *
 * Nothing native is changed. Just before the swap, this lays a copy of the
 * photo on screen over the slideshow and fades it out, so the old photo
 * dissolves into the new one and for a moment both are visible. `pointerover`
 * and `pointerout` are used because they are dispatched before `pointerenter`
 * and `pointerleave`, which is what guarantees the copy is taken before the
 * theme scrolls. If this file fails to load, the cards behave as they always
 * did.
 */

// Longer than the reference's 200ms on purpose: the user asked for the moment
// where both photos show to be noticeable, and at 200 it barely is.
const DURATION = 300;
const EASING = 'ease-in-out';
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/** @type {WeakMap<Element, HTMLElement>} */
const overlays = new WeakMap();

/**
 * The image of the slide currently in view.
 * @param {Element} container
 * @returns {HTMLImageElement | null}
 */
function visibleImage(container) {
  const frame = container.getBoundingClientRect();

  for (const slide of container.querySelectorAll('slideshow-slide')) {
    const box = slide.getBoundingClientRect();
    if (Math.abs(box.left - frame.left) < 2 && box.width > 0) {
      const image = slide.querySelector('img');
      return image instanceof HTMLImageElement ? image : null;
    }
  }

  return null;
}

/**
 * @param {PointerEvent} event
 */
function onCross(event) {
  if (event.pointerType !== 'mouse' || reducedMotion.matches) return;
  if (!(event.target instanceof Element)) return;

  const gallery = event.target.closest('.card-gallery');
  if (!gallery) return;

  // Moving between two children of the same gallery is not entering or leaving it.
  if (event.relatedTarget instanceof Node && gallery.contains(event.relatedTarget)) return;

  const container = gallery.querySelector('slideshow-container');
  if (!container || container.querySelectorAll('slideshow-slide').length < 2) return;

  const image = visibleImage(container);
  if (!image || !image.complete) return;

  overlays.get(container)?.remove();

  const copy = /** @type {HTMLImageElement} */ (image.cloneNode(false));
  const style = getComputedStyle(image);

  // The exact file already on screen, not the srcset: a copy left to pick its
  // own candidate (with `sizes="auto"` on a lazy image) had no source for the
  // whole fade, measured on the way back to the first photo.
  copy.removeAttribute('srcset');
  copy.removeAttribute('sizes');
  copy.removeAttribute('id');
  copy.removeAttribute('loading');
  copy.src = image.currentSrc || image.src;
  copy.setAttribute('aria-hidden', 'true');
  copy.alt = '';
  copy.className = 'mm-card-crossfade';
  Object.assign(copy.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    objectFit: style.objectFit,
    objectPosition: style.objectPosition,
    pointerEvents: 'none',
    zIndex: '1',
  });

  container.append(copy);
  overlays.set(container, copy);

  const fade = copy.animate([{ opacity: 1 }, { opacity: 0 }], { duration: DURATION, easing: EASING, fill: 'forwards' });
  const done = () => {
    copy.remove();
    if (overlays.get(container) === copy) overlays.delete(container);
  };

  fade.finished.then(done, done);
}

document.addEventListener('pointerover', onCross, { capture: true, passive: true });
document.addEventListener('pointerout', onCross, { capture: true, passive: true });
